// CognitiveFamily — the families that cluster ingest stages by their
// underlying cognitive operation, not their data type (Session 19Z architecture).
//
// AI_INGEST_STRATEGY.md §2 论证：
//   - Generative tasks (cases / flashcards / glossary / mindmaps / lecture_notes)
//     are homogeneous: read source → restructure as facts. Can be merged within
//     a chapter scope.
//   - Discrimination tasks (mcq / tf / proof_drill) require *reverse* cognition:
//     "decide what's wrong, design tempting wrong answers." Must be isolated
//     from Generative; mixing produces lazy distractors.
//   - Synthesis tasks (essay) require expanded argumentation chains + counter-
//     arguments. Must use thinking-mode model. Independent stage.
//   - Mock tasks need to AVOID overlap with prior 3 families' output (anti-
//     plagiarism for past_paper_strict mode). Must run last and consume
//     everything else as "inspiration pool to avoid."
//
// 19BH Day 2 Step 2.1 · Schema 开放化:
//   `CognitiveFamilySchema` 从 z.enum([...]) 升级为 z.string().min(1).max(32) ·
//   理由: v4 §5.3 + plan §5.1 · 未来 course template (e.g. STEM "evaluative",
//   外语 "comprehension") 可以注入自有 family · 不必为每个加 schema 改 enum。
//   TS 层 CognitiveFamily 类型仍是 5 个 canonical 的 literal union (避 dashboard
//   FAMILY_ORDER[a] 索引 NaN regression) · 双轨刻意。
import { z } from 'zod';
/**
 * Canonical built-in cognitive families that ship with the engine.
 *
 * The Zod schema (CognitiveFamilySchema) is open (z.string()) so external
 * data can declare future families without schema rejection, but TS code
 * inside this repo continues to use the literal union below — keeping
 * Record<CognitiveFamily, ...> exhaustive checks intact (frontend dashboard
 * FAMILY_ORDER lookup, componentsByFamily return shape, etc.).
 *
 * Adding a built-in family:
 *   1. Append the id here.
 *   2. Add a FAMILY_ORDER row (else dashboard sort returns NaN for it).
 *   3. componentsByFamily auto-picks it up (uses ALL_COGNITIVE_FAMILIES).
 */
export const KNOWN_COGNITIVE_FAMILIES = [
    'generative', // A — read & restructure (cases, flashcards, glossary, mindmaps, lecture_notes)
    'discrimination', // B — design tests with tempting distractors (mcq, tf, proof_drill)
    'synthesis', // C — expand argumentation chains (essay)
    'mock', // D — composite, avoids prior families' content (mock_papers, parse_past_papers)
    /** runtime-only components (dashboard / wrong_answers / mock_exam shell) — no AI generation */
    'runtime',
];
/**
 * Open runtime schema — accepts any non-empty short string. Use to validate
 * external data (CourseDraft import, ingest result migration, future template
 * registry entries). Built-in MANIFEST entries always use a KNOWN value.
 *
 * Width cap (32 chars): keep family id printable + greppable.
 */
export const CognitiveFamilySchema = z.string().min(1).max(32);
export const ALL_COGNITIVE_FAMILIES = KNOWN_COGNITIVE_FAMILIES;
/** UI / sort ordering for layered presentation (AI_INGEST_STRATEGY §13).
 *  Used by dashboard family-grouped views and future PathBar. */
export const FAMILY_ORDER = {
    generative: 0,
    discrimination: 1,
    synthesis: 2,
    mock: 3,
    runtime: 4,
};
//# sourceMappingURL=cognitive-family.js.map